import React, { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { AnimatePresence, motion } from 'framer-motion'
import { Spinner } from 'grommet'
import { toast } from 'react-toastify'
import campaignInviteStore from '../../../mobx/campaignInvite'
import userMobx from '../../../mobx/user'
import briefR from '../../../mobx/brief'
import { ClientI } from '../../../types-project/Client'
import Button from '../Ui/v2/buttons/Button'
import {
  getMessageCredits,
  purchaseMessageCredits,
  sendCampaignInvites,
  MessagePack,
} from '../../services/campaignInvite'

const DEFAULT_TEMPLATE =
  'Bonjour [prenom], nous avons adoré votre profil et serions ravis de vous inviter à participer à notre campagne [campagne]. Au plaisir de collaborer avec vous \u270C\uFE0F'

const VARIABLES: Array<{ token: string; label: string }> = [
  { token: '[prenom]', label: 'Prénom' },
  { token: '[marque]', label: 'Marque' },
  { token: '[campagne]', label: 'Campagne' },
]

const genBatchId = () => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c = (window as any).crypto
    if (c?.randomUUID) return c.randomUUID() as string
  } catch {
    /* noop */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const renderPreview = (
  template: string,
  vars: { firstName: string; brandName: string; campaignName: string }
) =>
  template
    .replace(/\[prenom\]/gi, vars.firstName || 'Prénom')
    .replace(/\[marque\]/gi, vars.brandName || 'votre marque')
    .replace(/\[campagne\]/gi, vars.campaignName || 'votre campagne')

const InviteModal: React.FC = () => {
  const isOpen = campaignInviteStore.isModalOpen
  const creators = campaignInviteStore.creators
  const count = creators.length

  const userInfos = userMobx.getUserInfos() as ClientI | null
  const companyId =
    userMobx.me.data?._company?._id ?? userInfos?._company?._id ?? ''
  const brandId = userMobx.getSelectedBrandId()
  const brandName =
    userMobx.getSelectedBrand()?.name ??
    userMobx.getBrandSession()?.name ??
    'votre marque'

  const [view, setView] = useState<'compose' | 'buy'>('compose')
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE)
  const [briefId, setBriefId] = useState('')
  const [balance, setBalance] = useState<number | null>(null)
  const [packs, setPacks] = useState<MessagePack[]>([])
  const [loadingCredits, setLoadingCredits] = useState(false)
  const [sending, setSending] = useState(false)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const batchIdRef = useRef<string>(genBatchId())

  const activeBriefs = useMemo(
    () => (briefR.allBriefsBrand.data ?? []).filter((b) => b.status === 'active'),
    [briefR.allBriefsBrand.data]
  )

  // Chargement campagnes + solde a l'ouverture.
  useEffect(() => {
    if (!isOpen) return
    setView('compose')
    setTemplate(DEFAULT_TEMPLATE)
    batchIdRef.current = genBatchId()
    if (brandId) briefR.getAllBriefsFromBrand(brandId)
    if (companyId) {
      setLoadingCredits(true)
      getMessageCredits(companyId)
        .then((res) => {
          setBalance(res.balance)
          setPacks(res.packs ?? [])
        })
        .catch(() => setBalance(0))
        .finally(() => setLoadingCredits(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, brandId, companyId])

  // Preselectionne la campagne s'il n'y en a qu'une.
  useEffect(() => {
    if (isOpen && !briefId && activeBriefs.length === 1)
      setBriefId(activeBriefs[0]._id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeBriefs])

  if (!isOpen) return null

  const selectedCampaign = activeBriefs.find((b) => b._id === briefId)
  const cost = count
  const enoughCredits = balance !== null && balance >= cost
  const canSend =
    !!briefId && template.trim().length > 0 && count > 0 && enoughCredits && !sending

  const insertVariable = (token: string) => {
    const el = textareaRef.current
    if (!el) {
      setTemplate((prev) => `${prev}${token}`)
      return
    }
    const start = el.selectionStart ?? template.length
    const end = el.selectionEnd ?? template.length
    const next = template.slice(0, start) + token + template.slice(end)
    setTemplate(next)
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  const handleSend = async () => {
    if (!brandId || !canSend) return
    setSending(true)
    try {
      const res = await sendCampaignInvites(brandId, {
        briefId,
        userIds: creators.map((c) => c.id),
        template,
        batchId: batchIdRef.current,
      })
      const skippedCount = res.skipped?.length ?? 0
      if (res.sent > 0) {
        toast.success(
          `${res.sent} invitation${res.sent > 1 ? 's' : ''} envoyée${
            res.sent > 1 ? 's' : ''
          }${skippedCount > 0 ? ` (${skippedCount} ignorée${skippedCount > 1 ? 's' : ''})` : ''}`,
          { theme: 'colored' }
        )
        campaignInviteStore.refreshBalance()
        campaignInviteStore.clear()
        campaignInviteStore.closeModal()
      } else {
        toast.info(
          skippedCount > 0
            ? 'Ces créateurs ont déjà été invités ou ont postulé à cette campagne.'
            : 'Aucune invitation envoyée.',
          { theme: 'colored' }
        )
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message
      if (msg === 'not-enough-message-credits') {
        toast.error('Crédits insuffisants.', { theme: 'colored' })
        setView('buy')
      } else if (msg === 'message-flagged') {
        // Modération IA : le message tente de sortir les créateurs de Vogz.
        // Aucun crédit consommé. On affiche le motif précis renvoyé par l'IA.
        const reason = e?.response?.data?.reason
        toast.error(
          `🚫 Message refusé par la modération${
            reason
              ? ` : ${reason}`
              : " : il semble inciter les créateurs à quitter Vogz."
          }\nReformulez votre invitation en restant dans le cadre de votre campagne Vogz (sans contact externe ni autre plateforme).`,
          { theme: 'colored', autoClose: 9000, style: { whiteSpace: 'pre-line' } }
        )
      } else if (msg === 'wrong-message') {
        toast.error(
          "Votre message contient une adresse e-mail. Les coordonnées externes ne sont pas autorisées.",
          { theme: 'colored', autoClose: 7000 }
        )
      } else if (msg === 'brief-not-active') {
        toast.error("Cette campagne n'est plus active.", { theme: 'colored' })
      } else {
        toast.error("Une erreur est survenue lors de l'envoi.", { theme: 'colored' })
      }
    } finally {
      setSending(false)
    }
  }

  const handlePurchase = async (packId: string) => {
    if (!companyId) return
    setPurchasing(packId)
    try {
      const { url } = await purchaseMessageCredits(
        companyId,
        packId,
        window.location.pathname
      )
      if (url) window.location.href = url
    } catch {
      toast.error("Impossible d'ouvrir le paiement.", { theme: 'colored' })
      setPurchasing(null)
    }
  }

  const previewName = creators[0]?.name?.split(' ')[0] ?? 'Prénom'

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => campaignInviteStore.closeModal()}
      >
        <motion.div
          className="relative flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="relative px-6 py-5 text-white"
            style={{ background: 'linear-gradient(135deg, #7885ed, #b06ef0)' }}
          >
            <button
              type="button"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
              onClick={() => campaignInviteStore.closeModal()}
              aria-label="Fermer"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <h2 className="text-xl font-bold">
              {view === 'compose'
                ? 'Inviter à une campagne'
                : 'Recharger des crédits message'}
            </h2>
            <p className="mt-1 text-sm text-white/90">
              {view === 'compose'
                ? `${count} créateur${count > 1 ? 's' : ''} sélectionné${count > 1 ? 's' : ''}`
                : 'Choisissez un pack adapté à vos envois'}
            </p>
          </div>

          {view === 'compose' ? (
            <div className="flex flex-1 flex-col overflow-y-auto px-6 py-5">
              {/* Createurs selectionnes */}
              <div className="mb-4 flex flex-wrap gap-2">
                {creators.map((c) => (
                  <span
                    key={c.id}
                    className="flex items-center gap-2 rounded-full bg-[#f1f3fe] py-1 pl-1 pr-2 text-sm text-[#3A4161]"
                  >
                    {c.picture ? (
                      <img
                        src={c.picture}
                        alt=""
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#7885ed] text-[10px] font-bold text-white">
                        {c.name?.charAt(0)?.toUpperCase() ?? '?'}
                      </span>
                    )}
                    {c.name?.split(' ')[0]}
                    <button
                      type="button"
                      onClick={() => campaignInviteStore.remove(c.id)}
                      className="ml-0.5 text-[#9aa0b5] hover:text-[#3A4161]"
                      aria-label="Retirer"
                    >
                      <i className="fa-solid fa-xmark text-xs" />
                    </button>
                  </span>
                ))}
              </div>

              {/* Campagne */}
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#9aa0b5]">
                Campagne
              </label>
              {briefR.allBriefsBrand.loading ? (
                <div className="mb-4 flex items-center gap-2 text-sm text-[#9aa0b5]">
                  <Spinner /> Chargement des campagnes…
                </div>
              ) : activeBriefs.length === 0 ? (
                <p className="mb-4 rounded-xl bg-[#fff4f4] p-3 text-sm text-[#d4574f]">
                  Aucune campagne active. Lancez une campagne avant d'inviter des
                  créateurs.
                </p>
              ) : (
                <select
                  value={briefId}
                  onChange={(e) => setBriefId(e.target.value)}
                  className="mb-4 w-full rounded-xl border border-[#e6e8f2] bg-white px-3 py-2.5 text-sm text-[#3A4161] outline-none focus:border-[#7885ed]"
                >
                  <option value="" disabled>
                    Sélectionnez une campagne…
                  </option>
                  {activeBriefs.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Message */}
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#9aa0b5]">
                Message
              </label>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {VARIABLES.map((v) => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => insertVariable(v.token)}
                    className="rounded-full border border-[#d7dbf7] bg-[#f7f8ff] px-2.5 py-1 text-xs font-medium text-[#7885ed] hover:bg-[#eef0ff]"
                  >
                    + {v.label}
                  </button>
                ))}
              </div>
              <textarea
                ref={textareaRef}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                rows={4}
                maxLength={1000}
                className="w-full resize-none rounded-xl border border-[#e6e8f2] p-3 text-sm text-[#3A4161] outline-none focus:border-[#7885ed]"
                placeholder="Votre message d'invitation…"
              />

              {/* Apercu */}
              <div className="mt-3 rounded-xl bg-[#f7f8ff] p-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9aa0b5]">
                  Aperçu
                </p>
                <p className="text-sm leading-snug text-[#3A4161]">
                  {renderPreview(template, {
                    firstName: previewName,
                    brandName,
                    campaignName: selectedCampaign?.name ?? '',
                  })}
                </p>
              </div>

              {/* Credits */}
              <div className="mt-4 flex items-center justify-between rounded-xl border border-[#e6e8f2] p-3 text-sm">
                <span className="text-[#3A4161]">
                  Coût : <b>{cost}</b> crédit{cost > 1 ? 's' : ''}
                </span>
                <span className="text-[#9aa0b5]">
                  {loadingCredits ? (
                    'Solde…'
                  ) : (
                    <>
                      Solde :{' '}
                      <b className={enoughCredits ? 'text-[#2bb673]' : 'text-[#d4574f]'}>
                        {balance ?? 0}
                      </b>
                    </>
                  )}
                </span>
              </div>
              {!loadingCredits && !enoughCredits && (
                <button
                  type="button"
                  onClick={() => setView('buy')}
                  className="mt-2 text-sm font-semibold text-[#7885ed] underline"
                >
                  Recharger des crédits
                </button>
              )}
            </div>
          ) : (
            // Vue achat de crédits
            <div className="flex flex-1 flex-col overflow-y-auto px-6 py-5">
              <button
                type="button"
                onClick={() => setView('compose')}
                className="mb-3 self-start text-sm font-semibold text-[#7885ed]"
              >
                <i className="fa-regular fa-angle-left mr-1" /> Retour
              </button>
              {packs.length === 0 ? (
                <p className="rounded-xl bg-[#fff4f4] p-3 text-sm text-[#d4574f]">
                  Aucun pack disponible pour le moment.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {packs.map((p) => (
                    <div
                      key={p.id}
                      className={`relative flex items-center justify-between rounded-2xl border p-4 ${
                        p.popular ? 'border-[#7885ed] bg-[#f7f8ff]' : 'border-[#e6e8f2]'
                      }`}
                    >
                      {p.popular && (
                        <span className="absolute -top-2 left-4 rounded-full bg-[#7885ed] px-2 py-0.5 text-[10px] font-bold text-white">
                          POPULAIRE
                        </span>
                      )}
                      <div>
                        <p className="font-bold text-[#3A4161]">{p.name}</p>
                        <p className="text-xs text-[#9aa0b5]">
                          {p.nb_credits} créateurs ·{' '}
                          {(p.price / p.nb_credits).toFixed(2)} €/message
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-[#3A4161]">
                          {p.price} €
                        </span>
                        <Button
                          variant="primary"
                          size="sm"
                          className="!w-auto !px-4"
                          isLoading={purchasing === p.id}
                          onclick={() => handlePurchase(p.id)}
                        >
                          Acheter
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-4 text-center text-xs text-[#9aa0b5]">
                Paiement sécurisé via Stripe. 1 crédit = 1 créateur invité.
              </p>
            </div>
          )}

          {/* Footer */}
          {view === 'compose' && (
            <div className="border-t border-[#eef0f6] px-6 py-4">
              <Button
                variant="primary"
                onclick={handleSend}
                disabled={!canSend}
                isLoading={sending}
              >
                {enoughCredits
                  ? `Envoyer ${count} invitation${count > 1 ? 's' : ''}`
                  : 'Crédits insuffisants'}
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default observer(InviteModal)
